import SidebarFloatingAd from 'components/ad/SidebarFloatingAd'
import NC from 'nc/NC'
import React, { useMemo } from 'react'
import { useAffilate } from 'shared-components/hooks/use-affilate'
import { useTaglessAd } from 'shared-components/hooks/use-tagless-ad'
import PostNavigation from 'shared-components/layout/PostNavigation'
import styleUtils from 'shared-components/styles/util.module.css'
import AdaptiveThreeColumnLayout from 'shared-components/ui/AdaptiveThreeColumnLayout'
import BestList from 'shared-components/ui/BestList'
import BreadCrumbs from 'shared-components/ui/BreadCrumbs'
import Disclaimer from 'shared-components/ui/Disclaimer'
import GoogleButtons from 'shared-components/ui/GoogleButtons'
import PostShortLine from 'shared-components/ui/PostShortLine'
import PostTags from 'shared-components/ui/PostTags'
import PostTitle from 'shared-components/ui/PostTitle'
import PromotedCard from 'shared-components/ui/PromotedCard'
import SidedCategoryMenu from 'shared-components/ui/SidedCategoryMenu'
import SourcesLine from 'shared-components/ui/SourcesLine'
import { DSBestListPageSG } from 'types.sg.ds'
import stylesCommon from './PostTemplate.ReviewTemplate.module.css'
import styles from './PostTemplate.module.css'
import { useArticleViewed } from 'shared-components/hooks/use-article-viewed'
import UnderTitleAds from 'components/ad/UnderTitleAds'
import ArticleConversation from 'shared-components/comments/conversation/ArticleConversation'
import { conversationSlot } from 'shared-components/comments/conversation/conversationSlot'
import ConversationRailCard from 'shared-components/comments/conversation/ConversationRailCard'
import {
  CatalogAnalyticsProvider,
  guideArticleSlug,
  type CatalogAnalyticsScope,
} from 'lib/analytics/catalog'
import { useDeclaredState } from 'lib/owner-notes/declared'
import { GuideOwnersProvider } from 'components/owner-notes/guide/GuideOwnersProvider'
import LabSurfaces, { useLabSurfacesDocked } from 'components/product/LabSurfaces'

interface BestListTemplateProps {
  page: DSBestListPageSG
}

const BestListBody: React.VFC<BestListTemplateProps> = ({ page }) => {
  useAffilate(page.productBars)
  useTaglessAd()
  // The mobile conversation bar yields while the LabCompare drawer / pill is docked (a pin, a
  // focus or a declared product) — not on every page with a strip.
  const labDocked = useLabSurfacesDocked()
  // The end-of-article conversation follows "Have your say" (nc-have-your-say) when the guide
  // has it (Guillaume, 2026-10-27) — else after the tags, as before.
  const conversationAt = conversationSlot(page.blocks)
  const conversation = page.meta.engagementContainerId ? (
    <ArticleConversation className={stylesCommon.comments} hideBar={labDocked} />
  ) : null

  return (
    <>
      <Disclaimer />
      <div
        className={styleUtils.wrapper}
        ref={useArticleViewed({
          articleId: page.meta?.postId?.toString() ?? null,
          title: page.title,
          url: page.pagePath,
          categoryLevel: 1,
          categoryTitle: page.meta.categories[0]?.label ?? '',
          author: page.authors.authoredBy.map(author => author.name).join(', '),
          wordCount: parseInt(page.meta.dataLayer?.word_count?.toString() ?? '0', 10),
          date: page.meta.dataLayer?.original_pub_date?.toString() ?? '',
        })}
      >
        <BreadCrumbs list={page.breadcrumbs} className={stylesCommon.breadCrumbs} />
        <PostTitle title={page.title} subtitle={page.subtitle} />
        {page.promotedProduct ? <PromotedCard data={page.promotedProduct} /> : null}
        <PostShortLine post={page} className={styles.shortLine} />
        <UnderTitleAds ads={page.underTitleAds} />
        <BestList bestList={page.bestList} />

        <AdaptiveThreeColumnLayout
          left={
            page.sideMenu.length ? (
              <SidedCategoryMenu
                className={stylesCommon.sidebarPostNav}
                title="Go deeper"
                menu={page.sideMenu}
              />
            ) : null
          }
          className={styles.content}
          content={
            <>
              <GoogleButtons data={page.googleButtons} />

              {page.blocks.map((item, index) => (
                <React.Fragment key={index + 1}>
                  <NC data={item} tpl="text" index={index} />
                  {index === conversationAt ? conversation : null}
                </React.Fragment>
              ))}
              {page.sources.length ? (
                <SourcesLine className={styleUtils.nc} sources={page.sources} />
              ) : null}

              <PostTags meta={page.meta} className={stylesCommon.postTags} />

              {conversationAt === -1 ? conversation : null}
            </>
          }
          sidebar={
            <PostNavigation>
              {[...page.sidebarContent, ...page.sidebarAds].map((item, index) => (
                <NC key={index} data={item} tpl="sidebar" index={index} />
              ))}
              {/* After every WordPress sidebar widget (the trust box arrives in sidebarAds as an
                  nc-multi), before the sticky Playwire ads: the widgets scroll away above the card and
                  never pass under the stuck strip; the ads still pin under it. */}
              <ConversationRailCard />
              <SidebarFloatingAd />
            </PostNavigation>
          }
        />
        {/* <RecommendSG
          containerClassName={stylesCommon.related}
          itemClassName={styleRelatedCard.relatedCard}
          title="More from SoundGuys"
        /> */}
      </div>
    </>
  )
}

/**
 * Owner notes on a best-list article (docs/owner-notes-guide-funnel-plan.md): the page-level
 * state the spliced blocks read — the analytics scope, the ONE declared product (#63) and the
 * lab-compare surfaces. The blocks themselves come from the mapper; this only mounts what they
 * share.
 */
const BestListTemplate: React.VFC<BestListTemplateProps> = props => {
  const { page } = props
  // Common params for every guide_page_* event (docs/analytics-catalog-events.md).
  // Memoised: the provider publishes this object as the page scope on identity change.
  const scope = useMemo<CatalogAnalyticsScope>(
    () => ({
      page_type: 'guide_page',
      // Omitted, never '': an empty string is a value in GA4 and would read as a category
      // named "" beside the real ones.
      ...(page.meta.categories[0]?.label ? { category: page.meta.categories[0].label } : {}),
      hub: 'best',
      scope: guideArticleSlug(page.pagePath),
    }),
    [page.meta.categories, page.pagePath]
  )
  // One declared-product state for the page: the capture block writes it, every pick block
  // reads it (owner-notes contract #63). Read after hydration only.
  const declared = useDeclaredState()
  return (
    <CatalogAnalyticsProvider value={scope}>
      {/* The lab dialog + pinned drawer mount on a declared pair OR a pin: a reader who has
          declared nothing but pinned two picks gets the drawer, and the dialog stays inert
          until something opens it. */}
      <GuideOwnersProvider page={page.guideOwners}>
        <LabSurfaces declared={declared} mountOnPin>
          <BestListBody {...props} />
        </LabSurfaces>
      </GuideOwnersProvider>
    </CatalogAnalyticsProvider>
  )
}

export default BestListTemplate
